/**
 * Coin and company logos the API holds (symbol → version), fetched once per page load when the first icon is
 * drawn. Public, so icons also work before sign-in. A logo's URL carries its version, so the browser caches it.
 */
import { useEffect } from 'react';
import { API_URL } from '../lib/config';
import { createStore, useStore } from '../lib/store';

const logosStore = createStore<{ versions: Record<string, number> | null; uploaded: string[] }>({ versions: null, uploaded: [] });
let started = false;

function load(fresh = false) {
  if (started && !fresh) return;
  started = true;
  const t = fresh ? `?t=${Date.now()}` : '';
  Promise.all([
    fetch(`${API_URL}/api/markets/logos${t}`).then((r) => (r.ok ? (r.json() as Promise<Record<string, number>>) : {})),
    fetch(`${API_URL}/api/markets/logos/uploaded${t}`).then((r) => (r.ok ? (r.json() as Promise<string[]>) : [])),
  ])
    .then(([versions, uploaded]) => logosStore.set({ versions, uploaded }))
    .catch(() => logosStore.set({ versions: {}, uploaded: [] }));
}

/** Fetch the list again, e.g. after an admin uploads or removes a logo. */
export const reloadLogos = () => load(true);

/** The logo URL for a symbol, or null when the API holds none (or the list hasn't loaded yet). */
/** Whether an admin uploaded this symbol's logo (it then wins over drawn marks and flags). */
export function useLogoUploaded(symbol: string): boolean {
  useEffect(() => load(), []);
  return useStore(logosStore, (s) => s.uploaded.includes(symbol));
}

export function useLogoUrl(symbol: string): string | null {
  useEffect(() => load(), []);
  const v = useStore(logosStore, (s) => s.versions?.[symbol]);
  return v ? `${API_URL}/api/markets/logo/${encodeURIComponent(symbol)}?v=${v}` : null;
}
